import { apiService } from "~/services/api";
import type {
  ICrew,
  ICrewActiveResponse,
  ICrewDataToken,
  IDetailTripCrew,
} from "~~/types/liveTracking/crew.interface";
import type { IApiError } from "~~/types/liveTracking/error.interface";
import type { IProcessedCrewData } from "../Tracking/useCrewTracking";
import { LOCAL_STORAGE_KEY } from "~~/constant/tracking.constant";

//ANCHOR state trip
const { getLastUpdate } = useUpdateBadge()
const crews = useState<ICrew | null>("crew-data", () => null);
const error = useState<IApiError | null>("error-message", () => null);
const { isLoading, incrementLoading, decrementLoading } = useLoadingState();
const tripDetailCrew = useState<IDetailTripCrew[]>("detail-trip-crew");
const activeCrews = useState<ICrewActiveResponse | null>(
  "active-crews",
  () => null,
);
const lastUpdate = ref(getLastUpdate(LOCAL_STORAGE_KEY.TrackingCrew))

//ANCHOR State crew data from token
const { getTokenData } = useAuth();
const crewDataToken = ref<ICrewDataToken>();

export const useCrew = () => {
  const fetchCrew = async (params?: Record<string, any>) => {
    error.value = null;
    incrementLoading();

    try {
      const response: any = await apiService.tracking.crew(params);

      if (response.error) {
        crews.value = null;
      } else if (response) {
        crews.value = response.data;
      }
    } catch (e: any) {
      error.value = e.message;
      console.error("Error fetching products:", e);
    } finally {
      decrementLoading();
    }
  };

  const fetchTripByNikCrew = async (t: IProcessedCrewData) => {
    error.value = null;
    incrementLoading();

    try {
      const response: any = await apiService.tracking.detailCrewTrip(t.nik);
      if (response.error) {
        error.value = {
          code: 0,
          error: response.error,
          timestamp: new Date(),
        };
      } else if (response) {
        tripDetailCrew.value = response.data.trips;
      }
    } catch (e: any) {
      error.value = e.message;
    } finally {
      decrementLoading();
    }
  };

  const fetchActiveCrew = async (params?: Record<string, any>) => {
    error.value = null;
    incrementLoading();

    try {
      const response: any = await apiService.tracking.activeCrew(params);

      activeCrews.value = response as ICrewActiveResponse;
    } catch (e: any) {
      activeCrews.value = null;
    } finally {
      decrementLoading();
    }
  };

  const fetchDetailCrew = async (nik: string) => {
    const response = await apiService.tracking.crewDetail(nik);
    if (response) {
      return response.data;
    } else {
      return null;
    }
  };

  const fetchCrewDetailsByNik = async (nik: string) => {
    try {
      const response = await apiService.tracking.crewNik(nik);
      return response.data?.name || null;
    } catch (error) {
      console.warn(
        `Could not fetch details for NIK ${nik}. Using placeholder name.`,
      );
      return null;
    }
  };

  const tokenData = () => {
    const tokenPayload = getTokenData();
    if (tokenPayload) {
      crewDataToken.value = tokenPayload;
    }
  };

  const storeLocation = computed(() => activeCrews.value?.data.store ?? null);

  return {
    fetchCrew,
    crewsData: crews,
    fetchTripByNikCrew,
    tripDetailCrew,
    fetchActiveCrew,
    drivers: activeCrews,
    storeLocation,
    isLoading,
    tokenData,
    crewDataToken,
    fetchDetailCrew,
    fetchCrewDetailsByNik,
    lastUpdate
  };
};
